import React, { memo } from "react";
import { Box, Typography } from "@mui/material";
import type { ArtifactTablePreview as Preview } from "../../../chat/types";

const MONEY = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function cellText(cell: unknown, money: boolean): string {
  if (cell === null || cell === undefined) return "";
  if (money && typeof cell === "number") return MONEY.format(cell);
  return String(cell);
}

/** Columns holding any non-whole number are money-shaped (the issues
 *  table's change read -78.5 and 120): all their numbers get two decimals,
 *  as the Excel export shows them. */
function moneyColumns(rows: unknown[][]): Set<number> {
  const out = new Set<number>();
  for (const row of rows) {
    row.forEach((cell, c) => {
      if (typeof cell === "number" && !Number.isInteger(cell)) out.add(c);
    });
  }
  return out;
}

/** The first rows of an export-only table, read-only: a glance at what the
 *  download holds, never a way to open the whole table (user 2026-10-05).
 *  The server sends at most five rows and ~4 KB, so this renders nothing big. */
export const ArtifactTablePreview = memo(function ArtifactTablePreview({
  preview,
}: {
  preview: Preview;
}) {
  const label = preview.row_label ?? "rows";
  const money = moneyColumns(preview.rows);
  return (
    <Box sx={{ mt: -0.5, mb: 1, px: 1 }}>
      <Box sx={{ overflowX: "auto" }}>
        <Box
          component="table"
          sx={{
            borderCollapse: "collapse",
            fontSize: "0.75rem",
            width: "100%",
            "& th, & td": {
              textAlign: "left",
              verticalAlign: "top",
              px: 1,
              py: 0.5,
              borderBottom: 1,
              borderColor: "divider",
            },
            "& th": { color: "text.secondary", fontWeight: 500, whiteSpace: "nowrap" },
          }}
        >
          <thead>
            <tr>
              {preview.header.map((h, i) => (
                <th key={i}>{cellText(h, false)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td key={c}>{cellText(cell, money.has(c))}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
        {`Showing ${preview.rows.length} of ${preview.total_rows} ${label} · Export for the full table`}
      </Typography>
    </Box>
  );
});
